import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const LabelManagementModal = ({ labels, onClose, onCreateLabel }) => {
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState('#3b82f6');
  const [isCreating, setIsCreating] = useState(false);

  const predefinedColors = [
    '#3b82f6', // blue
    '#10b981', // green
    '#f59e0b', // yellow
    '#ef4444', // red
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#f97316'  // orange
  ];

  const handleCreate = async () => {
    if (!newLabelName?.trim()) return;

    setIsCreating(true);
    try {
      await onCreateLabel(newLabelName?.trim(), newLabelColor);
      setNewLabelName('');
      setNewLabelColor('#3b82f6');
    } catch (err) {
      console.error('Error creating label:', err);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 p-2 rounded-lg">
              <Icon name="Tag" size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Manage Labels</h2>
              <p className="text-sm text-muted-foreground">Create and organize submission labels</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            onClick={onClose}
          />
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Create New Label */}
          <div className="bg-muted/50 border border-border rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-foreground mb-4">Create New Label</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Label Name
                </label>
                <Input
                  type="text"
                  placeholder="Enter label name"
                  value={newLabelName}
                  onChange={(e) => setNewLabelName(e?.target?.value)}
                  onKeyPress={(e) => e?.key === 'Enter' && handleCreate()}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Color
                </label>
                <div className="flex items-center gap-3">
                  {predefinedColors?.map((color) => (
                    <button
                      key={color}
                      className={`h-8 w-8 rounded-md transition-all ${
                        newLabelColor === color
                          ? 'ring-2 ring-ring ring-offset-2 ring-offset-background scale-110' :'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setNewLabelColor(color)}
                    />
                  ))}
                  <input
                    type="color"
                    value={newLabelColor}
                    onChange={(e) => setNewLabelColor(e?.target?.value)}
                    className="h-8 w-8 rounded-md cursor-pointer"
                  />
                </div>
              </div>

              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                loading={isCreating}
                onClick={handleCreate}
                disabled={!newLabelName?.trim()}
              >
                Create Label
              </Button>
            </div>
          </div>

          {/* Existing Labels */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">
              Existing Labels ({labels?.length})
            </h3>
            {labels?.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="Tag" size={48} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No labels created yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {labels?.map((label) => (
                  <div
                    key={label?.id}
                    className="flex items-center gap-3 p-3 bg-muted/50 border border-border rounded-lg"
                  >
                    <div
                      className="h-4 w-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: label?.color }}
                    />
                    <span className="text-sm font-medium text-foreground flex-1">
                      {label?.name}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LabelManagementModal;